<script setup lang="ts">
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Clock,
  ExternalLink,
  HelpCircle,
  Loader2,
  RefreshCw,
  XCircle
} from 'lucide-vue-next'

import type { DnsDiagnostic, DnsDiagnosticSummary } from '@/composables/domainConfig'
import type { Severity } from '@/components/base/BaseTip.vue'

const props = defineProps<{
  diagnostic: DnsDiagnostic | null
  isLoading: boolean
  hasFailed: boolean
}>()

const emit = defineEmits<{
  (e: 'refresh'): void
}>()

const { t, locale } = useI18n()

const MAILERSEND_GUIDE_URL = 'https://www.mailersend.com/help/how-to-verify-and-authenticate-a-sending-domain'

const providerName = computed(
  () => props.diagnostic?.provider?.name ?? t('pages.domain.config.steps.dns.diagnostic.yourDnsHost')
)

const guideUrl = computed(() => {
  const docs = props.diagnostic?.provider?.docs
  if (!docs) return MAILERSEND_GUIDE_URL
  return docs[locale.value] ?? docs.en ?? Object.values(docs)[0] ?? MAILERSEND_GUIDE_URL
})

const needsAction = computed(() =>
  ['nothing_created', 'incomplete', 'mismatch'].includes(props.diagnostic?.summary ?? '')
)

const summarySeverity: Record<DnsDiagnosticSummary, Severity> = {
  nothing_created: 'error',
  incomplete: 'warn',
  mismatch: 'warn',
  propagating: 'info',
  ready: 'success',
  unknown: 'secondary',
  domain_not_found: 'error'
}

const summaryIcons = {
  nothing_created: XCircle,
  incomplete: AlertTriangle,
  mismatch: AlertTriangle,
  propagating: Clock,
  ready: CheckCircle2,
  unknown: HelpCircle,
  domain_not_found: XCircle
}

const checkedAt = computed(() => {
  if (!props.diagnostic) return ''
  return new Date(props.diagnostic.checked_at).toLocaleTimeString(locale.value, {
    hour: '2-digit',
    minute: '2-digit'
  })
})
</script>

<template>
  <BaseTip v-if="isLoading && !diagnostic" severity="secondary" class="custom_message">
    <template #icon>
      <Loader2 class="animate-spin" />
    </template>
    <template #text>
      {{ t('pages.domain.config.steps.dns.diagnostic.loading') }}
    </template>
  </BaseTip>

  <BaseTip v-else-if="hasFailed" severity="secondary" class="custom_message">
    <template #text>
      <div>
        <span class="font-medium">{{ t('pages.domain.config.steps.dns.diagnostic.error.title') }}</span>
        <p class="mt-1">{{ t('pages.domain.config.steps.dns.diagnostic.error.description') }}</p>
      </div>
    </template>
    <template #button>
      <Button variant="outline" size="sm" class="w-max bg-white !border !border-border" @click="emit('refresh')">
        <RefreshCw class="h-4 w-4" />
        {{ t('pages.domain.config.steps.dns.diagnostic.retry') }}
      </Button>
    </template>
  </BaseTip>

  <div v-else-if="diagnostic" class="flex flex-col gap-4">
    <BaseTip :severity="summarySeverity[diagnostic.summary]" class="custom_message">
      <template #icon>
        <component :is="summaryIcons[diagnostic.summary]" />
      </template>
      <template #text>
        <div>
          <span class="font-medium">
            {{
              t(`pages.domain.config.steps.dns.diagnostic.summary.${diagnostic.summary}.title`, {
                provider: providerName,
                domain: diagnostic.domain
              })
            }}
          </span>
          <p class="mt-1">
            {{
              t(`pages.domain.config.steps.dns.diagnostic.summary.${diagnostic.summary}.description`, {
                provider: providerName,
                domain: diagnostic.domain
              })
            }}
          </p>
        </div>
      </template>
    </BaseTip>

    <div v-if="needsAction" class="border border-border rounded-lg bg-white p-4 flex flex-col gap-4">
      <p class="text-sm font-semibold text-foreground">
        {{ t('pages.domain.config.steps.dns.diagnostic.todo.title') }}
      </p>

      <ol class="flex flex-col gap-4">
        <li class="flex gap-3">
          <span
            class="flex items-center justify-center size-6 min-w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold"
          >
            1
          </span>
          <div class="flex flex-col gap-2">
            <p class="text-sm text-foreground">
              <template v-if="diagnostic.provider">
                {{ t('pages.domain.config.steps.dns.diagnostic.todo.step1', { provider: diagnostic.provider.name }) }}
              </template>
              <template v-else>
                {{ t('pages.domain.config.steps.dns.diagnostic.todo.step1Unknown') }}
                <span class="font-mono text-xs">{{ diagnostic.nameservers.join(', ') }}</span>
              </template>
            </p>
            <div>
              <Button
                v-if="diagnostic.provider"
                variant="outline"
                size="sm"
                as="a"
                :href="diagnostic.provider.manager_url"
                target="_blank"
                rel="noopener"
                class="bg-white !border !border-border"
              >
                <ExternalLink class="h-4 w-4" />
                {{
                  t('pages.domain.config.steps.dns.diagnostic.todo.managerButton', {
                    provider: diagnostic.provider.name
                  })
                }}
              </Button>
            </div>
          </div>
        </li>

        <li class="flex gap-3">
          <span
            class="flex items-center justify-center size-6 min-w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold"
          >
            2
          </span>
          <div class="flex flex-col gap-2">
            <p class="text-sm text-foreground">
              {{ t('pages.domain.config.steps.dns.diagnostic.todo.step2', { domain: diagnostic.domain }) }}
            </p>
            <div>
              <Button variant="default-primary" size="sm" as="a" :href="guideUrl" target="_blank" rel="noopener">
                <BookOpen class="h-4 w-4" />
                {{
                  diagnostic.provider
                    ? t('pages.domain.config.steps.dns.diagnostic.todo.guideButton', {
                        provider: diagnostic.provider.name
                      })
                    : t('pages.domain.config.steps.dns.diagnostic.todo.genericGuideButton')
                }}
              </Button>
            </div>
          </div>
        </li>

        <li class="flex gap-3">
          <span
            class="flex items-center justify-center size-6 min-w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold"
          >
            3
          </span>
          <div class="flex flex-col gap-2">
            <p class="text-sm text-foreground">{{ t('pages.domain.config.steps.dns.diagnostic.todo.step3') }}</p>
            <div>
              <Button
                variant="outline"
                size="sm"
                :disabled="isLoading"
                class="bg-white !border !border-border"
                @click="emit('refresh')"
              >
                <Loader2 v-if="isLoading" class="h-4 w-4 animate-spin" />
                <RefreshCw v-else class="h-4 w-4" />
                {{ t('pages.domain.config.steps.dns.diagnostic.recheck') }}
              </Button>
            </div>
          </div>
        </li>
      </ol>
    </div>

    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
      <p class="text-xs text-muted-foreground">
        {{ t('pages.domain.config.steps.dns.diagnostic.checkedAt', { time: checkedAt }) }}
        <span v-if="!diagnostic.authoritative_reachable">
          {{ t('pages.domain.config.steps.dns.diagnostic.authoritativeUnreachable') }}
        </span>
      </p>
      <div class="flex flex-wrap gap-2">
        <Button v-if="!needsAction" variant="ghost" size="sm" :disabled="isLoading" @click="emit('refresh')">
          <Loader2 v-if="isLoading" class="h-4 w-4 animate-spin" />
          <RefreshCw v-else class="h-4 w-4" />
          {{ t('pages.domain.config.steps.dns.diagnostic.recheck') }}
        </Button>
      </div>
    </div>
  </div>
</template>
